import { useEffect, useRef, useState } from 'react';

interface SectionTransitionProps {
  sectionKey: string;
  children: React.ReactNode;
}

export default function SectionTransition({ sectionKey, children }: SectionTransitionProps) {
  const [displayKey, setDisplayKey] = useState(sectionKey);
  const [phase, setPhase] = useState<'idle' | 'exit' | 'enter'>('idle');
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start exit transition when sectionKey changes
  const [prevSectionKey, setPrevSectionKey] = useState(sectionKey);
  if (sectionKey !== prevSectionKey && sectionKey !== displayKey) {
    setPrevSectionKey(sectionKey);
    setPendingKey(sectionKey);
    setPhase('exit');
  }

  useEffect(() => {
    if (phase !== 'exit') return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setDisplayKey(pendingKey!);
      setPhase('enter');

      timerRef.current = setTimeout(() => {
        setPhase('idle');
      }, 320);
    }, 180);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, pendingKey]);

  const getStyle = (): React.CSSProperties => {
    if (phase === 'exit') {
      return {
        opacity: 0,
        transform: 'translateY(10px) scale(0.99)',
        transition: 'opacity 180ms cubic-bezier(0.4,0,1,1), transform 180ms cubic-bezier(0.4,0,1,1)',
      };
    }
    if (phase === 'enter') {
      return {
        opacity: 0,
        transform: 'translateY(-8px) scale(0.99)',
        transition: 'none',
      };
    }
    return {
      opacity: 1,
      transform: 'translateY(0px) scale(1)',
      transition: 'opacity 320ms cubic-bezier(0.0,0,0.2,1), transform 320ms cubic-bezier(0.0,0,0.2,1)',
    };
  };

  return (
    <div style={{ ...getStyle(), willChange: 'opacity, transform', height: '100%' }}>
      {children}
    </div>
  );
}
